import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft,
  Copy,
  CheckCircle,
  Loader2,
  Mail,
  Edit,
  Download
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CoverLetterPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [letterContent, setLetterContent] = useState('');
  const [editing, setEditing] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleDownloadTxt = () => {
    const blob = new Blob([letterContent], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lettre_de_motivation.txt';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadWord = () => {
    const escaped = letterContent.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><title>Lettre de motivation</title><style>body{font-family:Calibri,Arial,sans-serif;font-size:11pt;line-height:1.6;margin:3cm 2.5cm;}pre{font-family:Calibri,Arial,sans-serif;font-size:11pt;white-space:pre-wrap;}</style></head><body><pre>${escaped}</pre></body></html>`;
    const blob = new Blob([html], { type: 'application/msword' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lettre_de_motivation.doc';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    loadAnalysis();
  }, [id]);

  const loadAnalysis = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/analysis/${id}`, {
        withCredentials: true
      });
      setAnalysis(data);
      // Strip any legacy disclaimer injected by the backend prompt
      const raw = data.cover_letter || '';
      const stripped = raw.replace(/\s*---\s*[\u26a0\ufe0f⚠️]*\s*Cette lettre a été générée[\s\S]*/i, '').trim();
      setLetterContent(stripped);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(letterContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!analysis?.cover_letter) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <Header />
        <div className="pt-32 pb-20 text-center">
          <p className="text-zinc-600">Lettre de motivation non disponible</p>
          <Button onClick={() => navigate(`/resultats/${id}`)} className="mt-4">
            Retour aux résultats
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Header />
      
      <div className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Back button */}
          <button 
            onClick={() => navigate(`/resultats/${id}`)}
            className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-6"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour aux résultats
          </button>

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
                <Mail className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h1 
                  className="text-2xl font-semibold text-zinc-900"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  data-testid="letter-title"
                >
                  Lettre de motivation
                </h1>
                <p className="text-zinc-500 text-sm">Personnalisée pour l'offre</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setEditing(!editing)}
                className="border-zinc-200"
                data-testid="edit-button"
              >
                <Edit className="h-4 w-4 mr-2" />
                {editing ? 'Aperçu' : 'Modifier'}
              </Button>
              <Button
                variant="outline"
                onClick={handleCopy}
                className="border-zinc-200"
                data-testid="copy-button"
              >
                {copied ? (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                    Copié !
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copier
                  </>
                )}
              </Button>
              <Button
                onClick={handleDownloadWord}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="download-word-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Word (.doc)
              </Button>
              <Button
                variant="outline"
                onClick={handleDownloadTxt}
                className="border-zinc-200"
                data-testid="download-txt-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Texte (.txt)
              </Button>
            </div>
          </div>

          {/* Letter Content */}
          <div className="bg-white border border-zinc-200 rounded-xl p-8" data-testid="letter-content">
            {editing ? (
              <Textarea
                value={letterContent}
                onChange={(e) => setLetterContent(e.target.value)}
                className="min-h-[500px] border-zinc-200"
                data-testid="letter-editor"
              />
            ) : (
              <div className="prose prose-zinc max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-zinc-700 leading-relaxed">
                  {letterContent}
                </pre>
              </div>
            )}
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CoverLetterPage;
